import './running_tasks.style.scss';

import { Input, Select, Table, Tag, Tooltip, Typography } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';

import type { RunningTask } from '../variant_workflow/redux';
import {
  RUNNING_TASK_LABELS,
  RUNNING_TASK_STATUS_TAG_COLORS,
} from './constants';
import { useRunningTasks } from './hooks/useRunningTasks';

const formatDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : '-';

const TaskVariantCell = ({ task }: { task: RunningTask }) => {
  if (!task.variant_id) {
    return <span>{task.description}</span>;
  }

  return (
    <Link to={`/variant-workflow/${task.variant_id}`}>
      {task.variant_id} ({task.variant_hgvs})
    </Link>
  );
};

const ErrorMessageCell = ({
  errorMessage,
}: {
  errorMessage?: string | null;
}) => {
  if (!errorMessage) {
    return <span>-</span>;
  }

  return (
    <Tooltip
      title={errorMessage}
      overlayClassName="running-tasks-page__error-tooltip"
    >
      <span className="running-tasks-page__error-text">{errorMessage}</span>
    </Tooltip>
  );
};

const RunningTasks = () => {
  const {
    filteredTasks,
    isLoading,
    availableTaskTypes,
    statusFilter,
    setStatusFilter,
    taskTypeFilter,
    setTaskTypeFilter,
    search,
    setSearch,
  } = useRunningTasks();

  const taskColumns = [
    {
      title: 'Task',
      dataIndex: 'task_type',
      render: (taskType: string) => RUNNING_TASK_LABELS[taskType] ?? taskType,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status: RunningTask['status']) => (
        <Tag color={RUNNING_TASK_STATUS_TAG_COLORS[status]}>{status}</Tag>
      ),
    },
    { title: 'Triggered by', dataIndex: 'username' },
    {
      title: 'Variant',
      render: (_: unknown, task: RunningTask) => (
        <TaskVariantCell task={task} />
      ),
    },
    {
      title: 'Started',
      dataIndex: 'created',
      render: (value: string) => formatDateTime(value),
    },
    {
      title: 'Finished',
      dataIndex: 'completed_at',
      render: (value: string | null | undefined) => formatDateTime(value),
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      render: (errorMessage: string | null | undefined) => (
        <ErrorMessageCell errorMessage={errorMessage} />
      ),
    },
  ];

  return (
    <div className="running-tasks-page">
      <Typography.Title level={2}>Task Queue</Typography.Title>
      <div className="running-tasks-page__filters">
        <Select
          className="running-tasks-page__filter"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'running', label: 'Running' },
            { value: 'completed', label: 'Completed' },
            { value: 'failed', label: 'Failed' },
          ]}
        />
        <Select
          className="running-tasks-page__filter"
          value={taskTypeFilter}
          onChange={setTaskTypeFilter}
          options={[
            { value: 'all', label: 'All task types' },
            ...availableTaskTypes.map((taskType) => ({
              value: taskType,
              label: RUNNING_TASK_LABELS[taskType] ?? taskType,
            })),
          ]}
        />
        <Input
          className="running-tasks-page__filter"
          allowClear
          placeholder="Search (task ID, variant, user, description)"
          value={search.freeText}
          onChange={(event) =>
            setSearch((previous) => ({
              ...previous,
              freeText: event.target.value,
            }))
          }
        />
        <Input
          className="running-tasks-page__filter"
          allowClear
          placeholder="Error messages contain…"
          value={search.errorText}
          onChange={(event) =>
            setSearch((previous) => ({
              ...previous,
              errorText: event.target.value,
            }))
          }
        />
      </div>
      <Table
        rowKey="task_id"
        loading={isLoading}
        dataSource={filteredTasks}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        columns={taskColumns}
      />
    </div>
  );
};

export default RunningTasks;
